import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { TaskService } from 'src/app/core/service/task.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { GridViewManager } from 'src/app/core/model/grid-view-manager';
import { MessengerService } from 'src/app/core/service/messenger.service';
import { DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { GroupDescriptor } from '@progress/kendo-data-query';
import { Subscription } from 'rxjs';
import { TaskUtility } from 'src/app/task/task.utility';
import { SettingService } from 'src/app/core/service/setting.service';
import { TextProcessor } from 'src/app/core/utility/text.processor';

@Component({
  standalone: false,
  selector: 'app-event-task-list',
  templateUrl: './event-task-list.component.html',
  styleUrls: ['./event-task-list.component.scss']
})
export class EventTaskListComponent implements OnInit, OnDestroy
{
  @Input() public caseId: any;
  @Input() public eventItem: any;
  @Input() public enableAdvanced = false;

  @Output() public openTaskDialog = new EventEmitter();

  public view: GridViewManager;
  public count: number;
  public pageSizes = [5, 10, 25, 50, 100];
  public windowState = 'default';

  /**
   * Selected task for re-assignment.
   */
  public task = null;

  public priorityDialogOpened = false;
  public distinctPriorities: Array<any> = [];

  /**
   * Controls the Task detail modal
   */
  public taskOpened = false;

  private messagingAddedTaskSubscription: Subscription;

  constructor(
    private taskService: TaskService,
    private messageHandlerService: MessageHandlerService,
    private messengerService: MessengerService,
    private taskUtilityService: TaskUtility,
    private settingService: SettingService
  )
  {
    this.view = new GridViewManager(5);
    this.messagingAddedTaskSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        // Refreshing the task list only for the matching event id.
        if (message.type === 'addedTaskEvent' && message.data.eventId === this.eventItem.id)
        {
          this.getTasks();
        }
      }
    });
  }

  public ngOnInit(): void
  {
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        const priorities = sessionSetting.taskPriorities.split(',');
        priorities.forEach(p =>
        {
          this.distinctPriorities.push({ priorityName: p, priority: p.toUpperCase() });
        });
      }
    });
    this.getTasks();
  }

  public ngOnDestroy(): void
  {
    if (this.messagingAddedTaskSubscription !== undefined) { this.messagingAddedTaskSubscription.unsubscribe(); }
  }

  public initiateTask(): void
  {
    this.eventItem.caseId = this.caseId;
    this.openTaskDialog.emit(this.eventItem);
  }

  /**
   * Get member tasks associated to an event.
   */
  public getTasks(): void
  {
    this.view.startLoading();
    this.taskService.getTasksForMemberEvent(this.eventItem.memberId, this.eventItem.id).subscribe({
      next: (data) =>
      {
        data.forEach(d =>
        {
          d.openedOn = new Date(d.openedOn);
          d.dueDate = d.dueDate != null ? new Date(d.dueDate) : null;
          d.priorityBadgeClass = this.taskUtilityService.priorityBadgeClass(d.priority);
        }
        );
        this.count = data.length;
        this.view.setData(data);
        this.view.setSort([{ field: 'openedOn', dir: 'desc' }]);
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      },
      complete: () =>
      {
        this.view.stopLoading();
      }
    });
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.view.setState(state);
  }

  /**
   * Group change event handler.
   *
   * @param groups List of GroupDescriptors
   */
  public groupChange(groups: GroupDescriptor[]): void
  {
    this.view.setGroup(groups);
  }

  /**
   * Sets the selected task, and opens the task priority dialog modal.
   *
   * @param task Selected task for re-assignment.
   */
  public openTaskPriorityDialog(task): void
  {
    this.task = task;
    this.priorityDialogOpened = true;
  }

  /**
   * Updates a task's priority.
   *
   * @param task Selected task for re-prioritize.
   * @param assigneeForm Form of the task-assign component.
   */
  public updateTaskPriority(task): void
  {
    this.task.priorityNumber = task.priorityNumber;
    this.task.priority = task.priority;
    this.task.priorityBadgeClass = this.taskUtilityService.priorityBadgeClass(task.priority);
    this.priorityDialogOpened = false;
  }

  public closeTaskPriorityDialog(): void
  {
    this.priorityDialogOpened = false;
  }

  /**
   * Opens the task detail modal.
   *
   * @param task Task object
   */
  public openTask(task: any): void
  {
    this.task = task;
    this.task.name = TextProcessor.truncateText(this.task.name, 70);
    this.taskOpened = true;
    document.body.style.overflow = 'hidden';
  }

  public closeTaskDialog(): void
  {
    this.taskOpened = false;
    document.body.style.overflow = '';
  }

  /**
   * Event handler for task completion.
   *
   * @param taskId Task ID
   */
  public onTaskCompleted(): void
  {
    this.closeTaskDialog();
    this.getTasks();
  }
}
